/**
 * Service Supabase pour la gestion des zones de prospection
 */

import { supabase } from '../../config/supabase';
import type {
  ProspectionZone,
  CreateProspectionZoneData,
  UpdateProspectionZoneData,
  ProspectionZoneFilters
} from '../../types/prospectionZone';

// Conversion snake_case (DB) vers camelCase (TypeScript)
function dbToZone(dbZone: any): ProspectionZone {
  return {
    id: dbZone.id,
    agencyId: dbZone.agency_id,
    userId: dbZone.user_id,
    userName: dbZone.user_name,
    name: dbZone.name,
    description: dbZone.description,
    color: dbZone.color,
    coordinates: dbZone.coordinates,
    isActive: dbZone.is_active,
    isVisible: dbZone.is_visible ?? true,
    createdAt: dbZone.created_at,
    updatedAt: dbZone.updated_at
  };
}

/**
 * Récupère toutes les zones de prospection
 */
export async function getProspectionZones(
  filters?: ProspectionZoneFilters
): Promise<ProspectionZone[]> {
  try {
    let query = supabase
      .from('prospection_zones')
      .select(`
        id,
        agency_id,
        user_id,
        name,
        description,
        color,
        coordinates,
        is_active,
        is_visible,
        created_at,
        updated_at,
        users!prospection_zones_user_id_fkey (
          name,
          first_name,
          email
        )
      `)
      .order('created_at', { ascending: false });

    // Filtres optionnels
    if (filters?.agencyId) {
      query = query.eq('agency_id', filters.agencyId);
    }

    if (filters?.activeOnly) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching prospection zones:', error);
      throw error;
    }

    // Transformer les données et ajouter le nom de l'utilisateur
    return (data || []).map((zone: any) => {
      const user = Array.isArray(zone.users) ? zone.users[0] : zone.users;
      const userName = user
        ? `${user.name || ''} ${user.first_name || ''}`.trim() || user.email
        : undefined;

      return dbToZone({
        ...zone,
        user_name: userName
      });
    });
  } catch (error) {
    console.error('Error in getProspectionZones:', error);
    throw error;
  }
}

/**
 * Récupère une zone de prospection par son ID
 */
export async function getProspectionZoneById(id: string): Promise<ProspectionZone | null> {
  try {
    const { data, error } = await supabase
      .from('prospection_zones')
      .select(`
        id,
        agency_id,
        user_id,
        name,
        description,
        color,
        coordinates,
        is_active,
        is_visible,
        created_at,
        updated_at,
        users!prospection_zones_user_id_fkey (
          name,
          first_name,
          email
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching prospection zone:', error);
      throw error;
    }

    if (!data) return null;

    const user = Array.isArray(data.users) ? data.users[0] : data.users;
    const userName = user
      ? `${user.name || ''} ${user.first_name || ''}`.trim() || user.email
      : undefined;

    return dbToZone({
      ...data,
      user_name: userName
    });
  } catch (error) {
    console.error('Error in getProspectionZoneById:', error);
    throw error;
  }
}

/**
 * Crée une nouvelle zone de prospection
 */
export async function createProspectionZone(
  zoneData: CreateProspectionZoneData,
  agencyId: string,
  userId: string
): Promise<ProspectionZone> {
  try {
    // Validation: minimum 3 points pour un polygone
    if (zoneData.coordinates.length < 3) {
      throw new Error('Une zone doit avoir au minimum 3 points');
    }

    const insertData: any = {
      agency_id: agencyId,
      user_id: userId,
      name: zoneData.name,
      description: zoneData.description,
      color: zoneData.color,
      coordinates: zoneData.coordinates, // Pas de JSON.stringify, Supabase gère le JSONB automatiquement
      is_active: zoneData.isActive ?? true,
      is_visible: zoneData.isVisible ?? true
    };

    // Ajouter la date de création personnalisée si fournie
    if (zoneData.createdAt) {
      insertData.created_at = zoneData.createdAt;
    }

    const { data, error } = await supabase
      .from('prospection_zones')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error creating prospection zone:', error);
      throw error;
    }

    return dbToZone(data);
  } catch (error) {
    console.error('Error in createProspectionZone:', error);
    throw error;
  }
}

/**
 * Met à jour une zone de prospection existante
 */
export async function updateProspectionZone(
  id: string,
  updates: UpdateProspectionZoneData
): Promise<ProspectionZone> {
  try {
    // Validation: si coordinates est fourni, minimum 3 points
    if (updates.coordinates && updates.coordinates.length < 3) {
      throw new Error('Une zone doit avoir au minimum 3 points');
    }

    const updateData: any = {};

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.color !== undefined) updateData.color = updates.color;
    if (updates.coordinates !== undefined) {
      // Pas de JSON.stringify, Supabase gère le JSONB automatiquement
      updateData.coordinates = updates.coordinates;
    }
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
    if (updates.isVisible !== undefined) updateData.is_visible = updates.isVisible;

    const { data, error } = await supabase
      .from('prospection_zones')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating prospection zone:', error);
      throw error;
    }

    return dbToZone(data);
  } catch (error) {
    console.error('Error in updateProspectionZone:', error);
    throw error;
  }
}

/**
 * Supprime une zone de prospection
 */
export async function deleteProspectionZone(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('prospection_zones')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting prospection zone:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteProspectionZone:', error);
    throw error;
  }
}

/**
 * Active ou désactive une zone de prospection
 */
export async function toggleProspectionZone(
  id: string,
  isActive: boolean
): Promise<ProspectionZone> {
  return updateProspectionZone(id, { isActive });
}

/**
 * Bascule la visibilité d'une zone de prospection sur la carte
 */
export async function toggleZoneVisibility(
  id: string,
  isVisible: boolean
): Promise<ProspectionZone> {
  return updateProspectionZone(id, { isVisible });
}

/**
 * Vérifie si un point est à l'intérieur d'une zone (algorithme ray-casting)
 */
export function isPointInZone(
  point: [number, number],
  zoneCoordinates: [number, number][]
): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = zoneCoordinates.length - 1; i < zoneCoordinates.length; j = i++) {
    const [xi, yi] = zoneCoordinates[i];
    const [xj, yj] = zoneCoordinates[j];

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Compte le nombre de markers dans une zone
 */
export async function countMarkersInZone(zoneId: string): Promise<number> {
  try {
    // Récupérer la zone
    const zone = await getProspectionZoneById(zoneId);
    if (!zone) return 0;

    // Récupérer tous les markers de l'agence
    const { data: markers, error } = await supabase
      .from('markers')
      .select('id, coordinates')
      .eq('agency_id', zone.agencyId);

    if (error) {
      console.error('Error fetching markers:', error);
      throw error;
    }

    if (!markers) return 0;

    // Compter les markers dans la zone
    // Note: Cela nécessite une fonction PostGIS côté serveur pour être précis
    // Pour l'instant, on retourne 0 (à implémenter avec une fonction SQL)
    const count = 0;

    return count;
  } catch (error) {
    console.error('Error in countMarkersInZone:', error);
    return 0;
  }
}
